import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import FloatingContact from "../components/FloatingContact";
import SEOHead from "../components/SEOHead";
import { useLanguage } from "../contexts/LanguageContext";

const SITE_URL = "https://adrise-agency.com";

const CONTENT = {
    en: {
        title: "Privacy Policy",
        subtitle: "How Adrise collects, processes, protects, and stores your data.",
        seoTitle: "Adrise Privacy Policy — Data Processing and Security",
        seoDescription:
            "Learn what data Adrise collects, why it is processed, retention terms, security controls, and how to contact us for data rights requests.",
        sections: [
            {
                h: "1) Data we collect",
                p: "We may collect account details (email, company details when provided), service usage events, support communication data, billing references, and blockchain transaction metadata required for payment reconciliation.",
            },
            {
                h: "2) Purpose of processing",
                p: "Data is processed to provide account access, perform compliance checks, secure service operations, monitor abuse risk, and deliver billing, support, and legal communications.",
            },
            {
                h: "3) Legal basis",
                p: "Processing is based on contract performance, legitimate interest for platform security/fraud prevention, and legal obligations where applicable.",
            },
            {
                h: "4) Sharing and subprocessors",
                p: "Data may be shared with vetted technical providers required for core operations (e.g., infrastructure, communications, analytics, blockchain verification). We do not sell personal data.",
            },
            {
                h: "5) Security and retention",
                p: "We apply access controls, logging, and environment-based secret management. Data is retained only as long as needed for service continuity, compliance, dispute handling, and legal requirements.",
            },
            {
                h: "6) International usage",
                p: "Adrise serves global clients. By using the service, you acknowledge cross-border processing where technically required.",
            },
            {
                h: "7) Your rights",
                p: "Where applicable by law, you may request access, correction, deletion, or processing limitation of your personal data via official support channels.",
            },
            {
                h: "8) Contact for privacy requests",
                p: "Submit privacy and data rights requests through email or Telegram listed on the Contact page.",
            },
        ],
    },
    pt: {
        title: "Política de Privacidade",
        subtitle: "Como a Adrise coleta, processa, protege e armazena seus dados.",
        seoTitle: "Adrise Política de Privacidade — Processamento e Segurança",
        seoDescription:
            "Saiba quais dados a Adrise coleta, por que processa, prazos de retenção, controles de segurança e como solicitar seus direitos de dados.",
        sections: [
            {
                h: "1) Dados coletados",
                p: "Podemos coletar dados de conta (e-mail, dados de empresa quando fornecidos), eventos de uso, comunicação de suporte, referências de cobrança e metadados de transações blockchain para conciliação.",
            },
            {
                h: "2) Finalidade do tratamento",
                p: "Os dados são tratados para fornecer acesso, executar checks de compliance, manter segurança operacional, monitorar risco de abuso e entregar comunicações de cobrança/suporte/legais.",
            },
            {
                h: "3) Base legal",
                p: "O tratamento ocorre por execução contratual, interesse legítimo em segurança/prevenção de fraude e obrigações legais quando aplicável.",
            },
            {
                h: "4) Compartilhamento e suboperadores",
                p: "Os dados podem ser compartilhados com provedores técnicos auditados necessários para operações essenciais (infraestrutura, comunicação, analytics, verificação blockchain). Não vendemos dados pessoais.",
            },
            {
                h: "5) Segurança e retenção",
                p: "Aplicamos controles de acesso, logs e gestão de segredos por ambiente. Os dados são retidos apenas pelo período necessário para continuidade do serviço, compliance, disputas e obrigações legais.",
            },
            {
                h: "6) Uso internacional",
                p: "A Adrise atende clientes globais. Ao usar o serviço, você reconhece processamento transfronteiriço quando tecnicamente necessário.",
            },
            {
                h: "7) Seus direitos",
                p: "Quando aplicável por lei, você pode solicitar acesso, correção, exclusão ou limitação de tratamento de dados pessoais pelos canais oficiais.",
            },
            {
                h: "8) Contato para privacidade",
                p: "Envie solicitações de privacidade por e-mail ou Telegram listados na página de contato.",
            },
        ],
    },
    es: {
        title: "Política de Privacidad",
        subtitle: "Cómo Adrise recopila, procesa, protege y almacena tus datos.",
        seoTitle: "Adrise Política de Privacidad — Tratamiento y Seguridad",
        seoDescription:
            "Conoce qué datos recopila Adrise, por qué se procesan, plazos de conservación, controles de seguridad y cómo ejercer tus derechos.",
        sections: [
            {
                h: "1) Datos que recopilamos",
                p: "Podemos recopilar datos de cuenta (email, datos de empresa cuando se faciliten), eventos de uso, comunicaciones de soporte, referencias de facturación y metadatos blockchain para conciliación.",
            },
            {
                h: "2) Finalidad del tratamiento",
                p: "Procesamos datos para dar acceso, ejecutar controles de compliance, proteger operaciones, monitorizar abuso y gestionar comunicaciones de facturación, soporte y legales.",
            },
            {
                h: "3) Base legal",
                p: "El tratamiento se basa en ejecución contractual, interés legítimo para seguridad y prevención de fraude, y obligaciones legales cuando corresponda.",
            },
            {
                h: "4) Compartición y subprocesadores",
                p: "Los datos pueden compartirse con proveedores técnicos auditados necesarios para operaciones núcleo (infraestructura, comunicación, analítica, verificación blockchain). No vendemos datos personales.",
            },
            {
                h: "5) Seguridad y conservación",
                p: "Aplicamos controles de acceso, logging y gestión de secretos por entorno. Conservamos datos solo el tiempo necesario para continuidad del servicio, compliance, disputas y exigencias legales.",
            },
            {
                h: "6) Uso internacional",
                p: "Adrise opera globalmente. Al usar el servicio, reconoces el tratamiento transfronterizo cuando sea técnicamente necesario.",
            },
            {
                h: "7) Tus derechos",
                p: "Cuando la ley aplique, puedes solicitar acceso, rectificación, eliminación o limitación del tratamiento mediante los canales oficiales de soporte.",
            },
            {
                h: "8) Contacto de privacidad",
                p: "Envía solicitudes de privacidad y derechos de datos por email o Telegram listados en la página de contacto.",
            },
        ],
    },
    ru: {
        title: "Политика конфиденциальности",
        subtitle: "Как Adrise собирает, обрабатывает, защищает и хранит данные.",
        seoTitle: "Adrise Политика конфиденциальности — Обработка и защита",
        seoDescription:
            "Узнайте, какие данные собирает Adrise, цели обработки, сроки хранения, меры безопасности и порядок запроса прав субъекта данных.",
        sections: [
            {
                h: "1) Какие данные мы собираем",
                p: "Мы можем собирать данные аккаунта (email, данные компании при указании), события использования сервиса, переписку с поддержкой, биллинговые сведения и метаданные блокчейн-транзакций для сверки.",
            },
            {
                h: "2) Цели обработки",
                p: "Данные обрабатываются для предоставления доступа, compliance-проверок, защиты операций, мониторинга злоупотреблений и поддержки биллинговых, сервисных и юридических коммуникаций.",
            },
            {
                h: "3) Правовые основания",
                p: "Основания: исполнение договора, законный интерес по безопасности/антифроду и юридические обязательства при необходимости.",
            },
            {
                h: "4) Передача и подрядчики",
                p: "Данные могут передаваться проверенным техническим провайдерам для базовых процессов (инфраструктура, коммуникации, аналитика, блокчейн-верификация). Персональные данные не продаются.",
            },
            {
                h: "5) Безопасность и сроки хранения",
                p: "Применяются контроль доступа, аудит и управление секретами по средам. Данные хранятся только на период, необходимый для работы сервиса, compliance, урегулирования споров и закона.",
            },
            {
                h: "6) Международная обработка",
                p: "Adrise обслуживает клиентов глобально. Используя сервис, вы признаете трансграничную обработку там, где это технически необходимо.",
            },
            {
                h: "7) Ваши права",
                p: "Если это предусмотрено законом, вы можете запросить доступ, исправление, удаление или ограничение обработки персональных данных через официальные каналы.",
            },
            {
                h: "8) Контакт по вопросам приватности",
                p: "Запросы по приватности и правам данных отправляйте через email или Telegram из страницы контактов.",
            },
        ],
    },
    tr: {
        title: "Gizlilik Politikası",
        subtitle: "Adrise verilerinizi nasıl toplar, işler, korur ve saklar.",
        seoTitle: "Adrise Gizlilik Politikası — Veri İşleme ve Güvenlik",
        seoDescription:
            "Adrise’ın hangi verileri topladığı, neden işlediği, saklama süreleri, güvenlik kontrolleri ve veri hakları başvuruları hakkında bilgi alın.",
        sections: [
            {
                h: "1) Toplanan veriler",
                p: "Hesap bilgileri (e-posta, varsa şirket bilgisi), hizmet kullanım kayıtları, destek iletişimi, faturalama referansları ve ödeme mutabakatı için gerekli blockchain işlem metadataları toplanabilir.",
            },
            {
                h: "2) İşleme amacı",
                p: "Veriler; hesap erişimi sağlamak, uyum kontrolleri yapmak, operasyon güvenliğini korumak, kötüye kullanım riskini izlemek ve faturalama/destek/hukuki iletişim sağlamak için işlenir.",
            },
            {
                h: "3) Hukuki dayanak",
                p: "İşleme; sözleşmenin ifası, platform güvenliği ve dolandırıcılık önleme için meşru menfaat, ayrıca gerektiğinde yasal yükümlülüklere dayanır.",
            },
            {
                h: "4) Paylaşım ve alt işleyiciler",
                p: "Veriler; temel operasyon için gerekli doğrulanmış teknik hizmet sağlayıcılarla (altyapı, iletişim, analitik, blockchain doğrulama) paylaşılabilir. Kişisel veriler satılmaz.",
            },
            {
                h: "5) Güvenlik ve saklama",
                p: "Erişim kontrolleri, loglama ve ortam bazlı secret yönetimi uygulanır. Veriler yalnızca hizmet sürekliliği, uyum, uyuşmazlık yönetimi ve yasal gereklilikler için gerekli süre kadar tutulur.",
            },
            {
                h: "6) Uluslararası kullanım",
                p: "Adrise küresel müşterilere hizmet verir. Hizmeti kullanarak teknik gereklilik olan durumlarda sınır ötesi veri işlemeyi kabul etmiş olursunuz.",
            },
            {
                h: "7) Veri haklarınız",
                p: "Yasanın uygulandığı kapsamda; erişim, düzeltme, silme veya işlemeyi kısıtlama taleplerinizi resmi destek kanalları üzerinden iletebilirsiniz.",
            },
            {
                h: "8) Gizlilik iletişimi",
                p: "Gizlilik ve veri hakları taleplerinizi Contact sayfasındaki e-posta veya Telegram üzerinden iletin.",
            },
        ],
    },
};

export default function PrivacyPage() {
    const { language } = useLanguage();
    const c = CONTENT[language] || CONTENT.en;
    const jsonLd = {
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: c.title,
        url: `${SITE_URL}/privacy`,
        inLanguage: language,
        isPartOf: { "@type": "WebSite", name: "Adrise", url: SITE_URL },
    };

    return (
        <main data-testid="privacy-page" className="bg-background text-foreground min-h-screen">
            <SEOHead
                title={c.seoTitle}
                description={c.seoDescription}
                canonical={`${SITE_URL}/privacy`}
                keywords={[
                    "adrise privacy",
                    "data protection policy",
                    "agency platform privacy",
                    "crypto payment data",
                ]}
                jsonLd={jsonLd}
                alternates={[
                    { hrefLang: "en", href: `${SITE_URL}/privacy` },
                    { hrefLang: "pt", href: `${SITE_URL}/privacy` },
                    { hrefLang: "es", href: `${SITE_URL}/privacy` },
                    { hrefLang: "ru", href: `${SITE_URL}/privacy` },
                    { hrefLang: "tr", href: `${SITE_URL}/privacy` },
                    { hrefLang: "x-default", href: `${SITE_URL}/privacy` },
                ]}
            />

            <Navbar />

            <section className="pt-32 md:pt-40 pb-16" data-testid="privacy-hero-section">
                <div className="container-skala">
                    <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight">
                        {c.title}
                    </h1>
                    <p className="text-sm md:text-base text-muted-foreground mt-4 max-w-3xl">
                        {c.subtitle}
                    </p>
                </div>
            </section>

            <section className="pb-20" data-testid="privacy-content-section">
                <div className="container-skala max-w-4xl space-y-7">
                    {c.sections.map((s) => (
                        <article key={s.h} className="bg-card border border-white/[0.08] rounded-2xl p-6 md:p-7" data-testid={`privacy-section-${s.h.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                            <h2 className="text-base md:text-lg font-semibold tracking-tight">{s.h}</h2>
                            <p className="text-sm md:text-base text-muted-foreground mt-3 leading-relaxed">{s.p}</p>
                        </article>
                    ))}
                </div>
            </section>

            <FloatingContact />
            <Footer />
        </main>
    );
}
